/**
 * screens/management/ManagementVersionHistory.js
 *
 * Version history viewer for the management portal.
 * Route: /management/history/:matchId
 *
 * Differences from the analyst-facing /history/:matchId screen:
 *   • Reads ONLY from Firestore — no IndexedDB fallback (managers use a
 *     different browser/device from analysts).
 *   • Shows an analyst selector so managers can switch between the analysts
 *     assigned to the match and view each one's checkpoint history.
 *   • No "Open InputBoard" shortcut — management view is read-only.
 */

import React, { useState } from "react";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import {
  Alert,
  Box,
  Chip,
  IconButton,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ArrowBack,
  CloudDone,
  History,
  Person,
  Refresh,
} from "@mui/icons-material";
import { useFirestoreCommitHistory } from "../../persistence/hooks/useFirestoreCommitHistory";
import BranchVcsPanel from "../components/BranchVcsPanel";

// ─── Root screen ─────────────────────────────────────────────────────────────

const ManagementVersionHistory = () => {
  const { matchId } = useParams();
  const navHistory  = useHistory();
  const location    = useLocation();

  // Prefer the match passed via router state from the management list (carries
  // analyst_details directly).  Fall back to the analyst-side Redux cache, which
  // the management portal does not populate — so it may be empty/stale here.
  const allMatches = useSelector((state) => state.match?.all_matches ?? []);
  const matchDetails =
    location.state?.match ??
    allMatches.find((m) => String(m.id) === String(matchId));

  const analysts = matchDetails?.analyst_details ?? [];

  const matchTitle = matchDetails
    ? `${matchDetails.team_a_details?.name ?? "?"} vs ${matchDetails.team_b_details?.name ?? "?"}`
    : `Match #${matchId}`;

  // Selected analyst tab — default to the first analyst
  const [selectedAnalystIdx, setSelectedAnalystIdx] = useState(0);
  const selectedAnalyst = analysts[selectedAnalystIdx] ?? null;

  const { versions, loading, error, refresh } = useFirestoreCommitHistory({
    matchId,
    analystId: selectedAnalyst ? String(selectedAnalyst.id) : null,
  });

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <Box
      className="version-history-page"
      sx={{
        height: "100vh",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
      }}
    >
      {/* ── Sticky header ──────────────────────────────────────────────────── */}
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, md: 4 },
          py: 2,
          borderBottom: "1px solid var(--border-1)",
          backgroundColor: "var(--surface-1)",
        }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          {/* Back to management */}
          <Tooltip title="Back to management">
            <IconButton
              onClick={() => navHistory.push("/management")}
              size="small"
              sx={{ border: "1px solid var(--border-1)", color: "var(--text-1)", flexShrink: 0 }}
            >
              <ArrowBack fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Title */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <History sx={{ fontSize: 18, color: "var(--text-2)", flexShrink: 0 }} />
              <Typography
                variant="h6"
                fontWeight={700}
                fontFamily="Poppins"
                color="var(--text-1)"
                noWrap
              >
                Version History
              </Typography>
              <Chip
                label="Management view · Firestore only"
                size="small"
                variant="outlined"
                icon={<CloudDone sx={{ fontSize: "14px !important" }} />}
                sx={{
                  fontSize: 10,
                  height: 22,
                  fontFamily: "Poppins",
                  borderColor: "var(--border-1)",
                  color: "var(--text-2)",
                  flexShrink: 0,
                }}
              />
            </Stack>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.25 }}>
              <Typography variant="body2" fontFamily="Poppins" color="var(--text-2)" noWrap>
                {matchTitle}
              </Typography>
              <Typography variant="body2" color="var(--text-2)">·</Typography>
              <Typography variant="body2" fontFamily="Poppins" color="var(--text-2)">
                Match&nbsp;#{matchId}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </Box>

      {/* ── Analyst tabs ───────────────────────────────────────────────────── */}
      {analysts.length > 0 && (
        <Box
          sx={{
            flexShrink: 0,
            borderBottom: "1px solid var(--border-1)",
            backgroundColor: "var(--surface-1)",
            px: { xs: 1, md: 3 },
          }}
        >
          <Tabs
            value={selectedAnalystIdx}
            onChange={(_, idx) => setSelectedAnalystIdx(idx)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              minHeight: 44,
              "& .MuiTab-root": {
                fontFamily: "Poppins",
                fontSize: 13,
                textTransform: "none",
                minHeight: 44,
                color: "var(--text-2)",
              },
              "& .Mui-selected": { color: "var(--primary-1) !important" },
              "& .MuiTabs-indicator": { backgroundColor: "var(--primary-1)" },
            }}
          >
            {analysts.map((analyst, idx) => (
              <Tab
                key={analyst.id}
                label={
                  <Stack direction="row" spacing={0.75} alignItems="center">
                    <Person sx={{ fontSize: 15 }} />
                    <span>{analyst.name || analyst.username}</span>
                    <Typography
                      variant="caption"
                      sx={{
                        opacity: 0.6,
                        fontFamily: "Poppins",
                        fontSize: 10,
                      }}
                    >
                      #{analyst.id}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Tabs>
        </Box>
      )}

      {/* ── Scrollable content ──────────────────────────────────────────────── */}
      <Box
        sx={{
          flex: 1,
          overflowY: "auto",
          px: { xs: 2, md: 4 },
          py: 2.5,
        }}
      >
        {/* No analysts assigned */}
        {analysts.length === 0 && (
          <Box
            sx={{
              border: "1px dashed var(--border-1)",
              borderRadius: 2,
              py: 10,
              px: 4,
              textAlign: "center",
              backgroundColor: "var(--surface-1)",
            }}
          >
            <Person sx={{ fontSize: 48, color: "var(--border-1)", mb: 2 }} />
            <Typography variant="h6" fontFamily="Poppins" fontWeight={600} color="var(--text-1)" mb={1}>
              No analysts assigned
            </Typography>
            <Typography variant="body2" fontFamily="Poppins" color="var(--text-2)">
              Assign analysts to this match to see their version history here.
            </Typography>
          </Box>
        )}

        {/* Analyst selected — show version history */}
        {selectedAnalyst && (
          <>
            {/* Analyst info + refresh */}
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              mb={2}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <CloudDone sx={{ fontSize: 15, color: "var(--text-2)" }} />
                <Typography variant="caption" color="var(--text-2)" fontFamily="Poppins">
                  Firestore cloud history for&nbsp;
                  <strong style={{ color: "var(--text-1)" }}>
                    {selectedAnalyst.name || selectedAnalyst.username}
                  </strong>
                </Typography>
              </Stack>
              <Tooltip title="Reload">
                <IconButton size="small" onClick={refresh} disabled={loading} sx={{ p: 0.5 }}>
                  <Refresh sx={{ fontSize: 16, color: "var(--text-2)" }} />
                </IconButton>
              </Tooltip>
            </Stack>

            {/* Error */}
            {error && !loading && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {/* Read-only version-control view — same "Saved versions" panel the
                analyst sees, but managers can only view + preview, not restore,
                combine, or push. */}
            <BranchVcsPanel
              readOnly
              matchId={matchId}
              analystId={String(selectedAnalyst.id)}
              commits={versions}
              loading={loading}
              title={selectedAnalyst.name || selectedAnalyst.username || "Analyst"}
            />

            <Box sx={{ height: 24 }} />
          </>
        )}
      </Box>
    </Box>
  );
};

export default ManagementVersionHistory;
